import { FC, useState } from "react";
import { Container, Grid, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../stores/user.store";
import Header from "../Dashboard/Header";
import BasicKPIComponent from "../Dashboard/BasicKPIComponent";
import SowsProgressionChart from "../../components/SowsProgressionChart";
import useDashboardKPISummary from "../../hooks/useDashboardKPISummary";
import useSowsProgression from "../../hooks/useSowsProgression";
import { DashboardDateRange } from "../../services/dashboard.service";

const GBP_FORMATTER = new Intl.NumberFormat("en-GB", {
	style: "currency",
	currency: "GBP",
});

const DATE_RANGE_MAP: Record<
	"lastMonth" | "lastQuarter" | "lastYear",
	DashboardDateRange
> = {
	lastMonth: "LAST_MONTH",
	lastQuarter: "LAST_QUARTER",
	lastYear: "LAST_YEAR",
};

const formatGBP = (value: number | undefined): string =>
	GBP_FORMATTER.format(value ?? 0);

const KPI_SKELETON = <Skeleton variant="text" width="60%" height={48} />;

interface Filters {
	programId: string;
	providerId: string;
	providerSiteId: string;
	search: string;
	pageNumber: number;
	pageSize: number;
	dateRange: "lastMonth" | "lastQuarter" | "lastYear";
	zipCode: string;
}

const DashboardB2D: FC = () => {
	const { userInfo, userHasAccessToCode } = useUserStore();
	const { t } = useTranslation("dashboard");

	const [filters, setFilters] = useState<Filters>({
		programId: "",
		providerId: "",
		providerSiteId: "",
		search: "",
		pageNumber: 0,
		pageSize: 10,
		dateRange: "lastMonth",
		zipCode: "",
	});

	const orgUnitIds = filters.providerSiteId
		? filters.providerSiteId.split(",").filter(Boolean)
		: [];

	const { data: kpiSummary, isLoading: isKpiLoading } = useDashboardKPISummary({
		accountId: userInfo?.accountId ?? "",
		programId: filters.programId,
		orgUnitIds,
		providerId: filters.providerId,
		dateRange: DATE_RANGE_MAP[filters.dateRange],
	});

	const { data: sowsProgression, isLoading: isSowsLoading } =
		useSowsProgression({
			accountId: userInfo?.accountId ?? "",
			programId: filters.programId,
			orgUnitIds,
			providerId: filters.providerId,
		});

	if (!userHasAccessToCode("DASH_VIEW_B2D")) {
		return null;
	}

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Header
				filtersState={[filters, setFilters]}
				// The Header gates the "Filter by program" select behind a
				// truthy `handleSearch` prop. The B2D dashboard re-fetches its
				// KPIs via React Query whenever `filters.programId` changes
				// (no imperative search step needed), so we pass a no-op just
				// to make the select render. SIMS-5737 AC requires this filter.
				handleSearch={() => undefined}
			/>

			<Typography variant="headlineSmall" sx={{ mb: 2 }}>
				Overview
			</Typography>

			<Grid container spacing={2} sx={{ mb: 4 }}>
				<BasicKPIComponent
					title={t("totalIncentivesEarned")}
					content={
						isKpiLoading
							? KPI_SKELETON
							: formatGBP(kpiSummary?.totalIncentivesEarned)
					}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title={t("totalIncentivesReceived")}
					content={
						isKpiLoading
							? KPI_SKELETON
							: formatGBP(kpiSummary?.totalIncentivesReceived)
					}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title={t("sowsSubmitted")}
					content={
						isKpiLoading ? KPI_SKELETON : (kpiSummary?.sowsSubmitted ?? 0)
					}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title={t("medicationProofSubmitted")}
					content={
						isKpiLoading
							? KPI_SKELETON
							: (kpiSummary?.medicationProofSubmitted ?? 0)
					}
					footer={{ hidden: true }}
				/>
			</Grid>

			<SowsProgressionChart
				title={t("sowsAverageScoringProgression")}
				data={sowsProgression?.map((p) => ({ day: p.day, score: p.avgScore }))}
				isLoading={isSowsLoading}
			/>
		</Container>
	);
};

export default DashboardB2D;
